import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { AlertTriangleIcon, SearchIcon, FilterIcon, ClockIcon, CheckCircleIcon, XCircleIcon, FileTextIcon, UploadIcon, CalendarIcon, CreditCardIcon, TrendingUpIcon, TrendingDownIcon, AlertCircleIcon, XIcon, ArrowUpRightIcon, CheckSquareIcon, UndoIcon, FileClockIcon, LoaderIcon, TrophyIcon, CheckIcon, MoreVerticalIcon, ArrowUpDownIcon, ArrowUpIcon, ArrowDownIcon, EyeIcon, InfoIcon } from 'lucide-react';
import { Dispute, DisputeStatus, DisputeReason, DisputeStage } from '../../../shared/types/Dispute';
import { generateDisputes } from '../utils/disputes';
type DateFilterType = 'response_due' | 'disputed_time' | 'last_updated';
type SortField = 'dueDate' | 'amount' | 'reason' | 'stage' | 'status' | 'paymentMethod' | 'customerName';
type SortDirection = 'asc' | 'desc' | null;
type QuickFilterType = 'requires_response' | 'challenged' | 'all' | 'won_reversed' | 'accepted_lost' | null;
export function Disputes() {
  const {
    t
  } = useTranslation('disputes');
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<DisputeStatus | 'all'>('all');
  const [reasonFilter, setReasonFilter] = useState<DisputeReason | 'all'>('all');
  const [stageFilter, setStageFilter] = useState<DisputeStage | 'all'>('all');
  const [paymentMethodFilter, setPaymentMethodFilter] = useState<string>('all');
  const [dateFilterType, setDateFilterType] = useState<DateFilterType>('response_due');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  const [activeQuickFilter, setActiveQuickFilter] = useState<QuickFilterType>('requires_response');
  const [selectedDisputes, setSelectedDisputes] = useState<Set<string>>(new Set());
  const [showBulkAcceptModal, setShowBulkAcceptModal] = useState(false);
  const [hoveredReason, setHoveredReason] = useState<string | null>(null);
  // Use centralized mock data
  const disputes = useMemo(() => generateDisputes(), []);
  // Calculate statistics
  const totalDisputes = disputes.length;
  const requiresResponseCount = disputes.filter(d => d.status === 'requires_response').length;
  const challengedCount = disputes.filter(d => d.status === 'challenged').length;
  const wonOrReversedCount = disputes.filter(d => d.status === 'won' || d.status === 'reversed').length;
  const acceptedOrLostCount = disputes.filter(d => d.status === 'accepted' || d.status === 'lost').length;
  // Get the last updated date for a dispute
  const getLastUpdated = (dispute: Dispute): string => {
    if (dispute.lifecycle && dispute.lifecycle.length > 0) {
      const lastEvent = dispute.lifecycle[dispute.lifecycle.length - 1];
      return lastEvent.timestamp;
    }
    return dispute.createdAt;
  };
  // Handle quick filter clicks
  const handleQuickFilter = (filterType: QuickFilterType) => {
    if (activeQuickFilter === filterType) {
      // Clicking the same filter again deactivates it
      setActiveQuickFilter(null);
      setStatusFilter('all');
    } else {
      setActiveQuickFilter(filterType);
      // Update the status filter based on quick filter
      switch (filterType) {
        case 'requires_response':
          setStatusFilter('requires_response');
          break;
        case 'challenged':
          setStatusFilter('challenged');
          break;
        case 'all':
          setStatusFilter('all');
          break;
        case 'won_reversed':
          // This will be handled in the filter logic
          setStatusFilter('all');
          break;
        case 'accepted_lost':
          // This will be handled in the filter logic
          setStatusFilter('all');
          break;
      }
    }
  };
  // Handle column sorting
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Cycle through: asc -> desc -> null
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        setSortField(null);
        setSortDirection(null);
      }
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  // Filter and sort disputes
  const filteredDisputes = useMemo(() => {
    let filtered = disputes.filter(dispute => {
      const matchesSearch = dispute.disputeId.toLowerCase().includes(searchQuery.toLowerCase()) || dispute.transactionId.toLowerCase().includes(searchQuery.toLowerCase()) || dispute.customerName.toLowerCase().includes(searchQuery.toLowerCase()) || dispute.customerEmail.toLowerCase().includes(searchQuery.toLowerCase());
      // Handle quick filter logic
      let matchesQuickFilter = true;
      if (activeQuickFilter === 'won_reversed') {
        matchesQuickFilter = dispute.status === 'won' || dispute.status === 'reversed';
      } else if (activeQuickFilter === 'accepted_lost') {
        matchesQuickFilter = dispute.status === 'accepted' || dispute.status === 'lost';
      } else if (activeQuickFilter && activeQuickFilter !== 'all') {
        matchesQuickFilter = dispute.status === activeQuickFilter;
      }
      const matchesStatus = statusFilter === 'all' || dispute.status === statusFilter;
      const matchesReason = reasonFilter === 'all' || dispute.reason === reasonFilter;
      const matchesStage = stageFilter === 'all' || dispute.stage === stageFilter;
      const matchesPaymentMethod = paymentMethodFilter === 'all' || dispute.paymentMethod === paymentMethodFilter;
      // Date filtering
      let matchesDateRange = true;
      if (dateFrom || dateTo) {
        let dateToCheck: Date;
        if (dateFilterType === 'response_due') {
          dateToCheck = new Date(dispute.dueDate);
        } else if (dateFilterType === 'disputed_time') {
          dateToCheck = new Date(dispute.createdAt);
        } else {
          dateToCheck = new Date(getLastUpdated(dispute));
        }
        if (dateFrom) {
          const fromDate = new Date(dateFrom);
          fromDate.setHours(0, 0, 0, 0);
          if (dateToCheck < fromDate) matchesDateRange = false;
        }
        if (dateTo) {
          const toDate = new Date(dateTo);
          toDate.setHours(23, 59, 59, 999);
          if (dateToCheck > toDate) matchesDateRange = false;
        }
      }
      return matchesSearch && matchesQuickFilter && matchesStatus && matchesReason && matchesStage && matchesPaymentMethod && matchesDateRange;
    });
    // Apply sorting
    if (sortField && sortDirection) {
      filtered.sort((a, b) => {
        let aValue: any;
        let bValue: any;
        switch (sortField) {
          case 'dueDate':
            aValue = new Date(a.dueDate).getTime();
            bValue = new Date(b.dueDate).getTime();
            break;
          case 'amount':
            aValue = a.amount;
            bValue = b.amount;
            break;
          case 'reason':
            aValue = getReasonLabel(a.reason);
            bValue = getReasonLabel(b.reason);
            break;
          case 'stage':
            aValue = getStageLabel(a.stage);
            bValue = getStageLabel(b.stage);
            break;
          case 'status':
            aValue = a.status;
            bValue = b.status;
            break;
          case 'paymentMethod':
            aValue = getPaymentMethodLabel(a.paymentMethod);
            bValue = getPaymentMethodLabel(b.paymentMethod);
            break;
          case 'customerName':
            aValue = a.customerName;
            bValue = b.customerName;
            break;
          default:
            return 0;
        }
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return sortDirection === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
        } else {
          return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
        }
      });
    } else {
      // Default sorting: Requires response first, then by closest deadline
      filtered.sort((a, b) => {
        const aRequiresResponse = a.status === 'requires_response';
        const bRequiresResponse = b.status === 'requires_response';
        if (aRequiresResponse && !bRequiresResponse) return -1;
        if (!aRequiresResponse && bRequiresResponse) return 1;
        const aDate = new Date(a.dueDate).getTime();
        const bDate = new Date(b.dueDate).getTime();
        return aDate - bDate;
      });
    }
    return filtered;
  }, [disputes, searchQuery, statusFilter, reasonFilter, stageFilter, paymentMethodFilter, dateFilterType, dateFrom, dateTo, sortField, sortDirection, activeQuickFilter]);
  const getStatusIcon = (status: DisputeStatus) => {
    const iconSize = 14;
    const iconClass = 'mr-1.5';
    switch (status) {
      case 'requires_response':
        return <AlertCircleIcon size={iconSize} className={iconClass} data-id="element-6013" />;
      case 'expired':
        return <XIcon size={iconSize} className={iconClass} data-id="element-6014" />;
      case 'challenged':
        return <ArrowUpRightIcon size={iconSize} className={iconClass} data-id="element-6015" />;
      case 'accepted':
        return <CheckSquareIcon size={iconSize} className={iconClass} data-id="element-6016" />;
      case 'reversed':
        return <UndoIcon size={iconSize} className={iconClass} data-id="element-6017" />;
      case 'pending_closure':
        return <FileClockIcon size={iconSize} className={iconClass} data-id="element-6018" />;
      case 'pending_decision':
        return <LoaderIcon size={iconSize} className={iconClass} data-id="element-6019" />;
      case 'won':
        return <TrophyIcon size={iconSize} className={iconClass} data-id="element-6020" />;
      case 'lost':
        return <XCircleIcon size={iconSize} className={iconClass} data-id="element-6021" />;
      default:
        return null;
    }
  };
  const getStatusBadge = (status: DisputeStatus) => {
    // Using exact colors from the screenshot
    const styles = {
      requires_response: 'text-[#92400E] bg-[#FFEDD5]',
      expired: 'text-[#374151] bg-[#E5E7EB]',
      challenged: 'text-[#1D4ED8] bg-[#DBEAFE]',
      accepted: 'text-[#7C2D12] bg-[#FFEDD5]',
      reversed: 'text-[#166534] bg-[#DCFCE7]',
      pending_closure: 'text-[#4B5563] bg-[#E5E7EB]',
      pending_decision: 'text-[#1D4ED8] bg-[#DBEAFE]',
      won: 'text-[#166534] bg-[#DCFCE7]',
      lost: 'text-[#B91C1C] bg-[#FEE2E2]'
    };
    const labels = {
      requires_response: 'Requires response',
      expired: 'Expired',
      challenged: 'Challenged',
      accepted: 'Accepted',
      reversed: 'Reversed',
      pending_closure: 'Pending Closure',
      pending_decision: 'Pending Decision',
      won: 'Won',
      lost: 'Lost'
    };
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status]}`} data-id="element-6022">
        {getStatusIcon(status)}
        {labels[status]}
      </span>;
  };
  const getStageLabel = (stage: DisputeStage) => {
    const labels = {
      rfi: 'RFI',
      pre_chargeback: 'Pre-chargeback',
      chargeback: 'Chargeback',
      pre_arbitration: 'Pre-arbitration',
      arbitration: 'Arbitration'
    };
    return labels[stage];
  };
  const getReasonLabel = (reason: DisputeReason) => {
    const labels = {
      duplicate: 'Duplicate',
      fraudulent: 'Fraudulent',
      subscription_canceled: 'Subscription canceled',
      product_unacceptable: 'Product unacceptable',
      product_not_received: 'Product not received',
      unrecognized: 'Unrecognized',
      credit_not_processed: 'Credit not processed',
      incorrect_account_details: 'Incorrect account details',
      insufficient_funds: 'Insufficient funds',
      bank_cannot_process: 'Bank cannot process',
      debit_not_authorized: 'Debit not authorized',
      general: 'General',
      customer_initiated: 'Customer initiated',
      check_returned: 'Check returned',
      noncompliant: 'Noncompliant'
    };
    return labels[reason];
  };
  const getPaymentMethodLabel = (method: string) => {
    const labels = {
      card: 'Card',
      bank_transfer: 'Bank Transfer',
      wallet: 'Wallet'
    };
    return labels[method] || method;
  };
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = date.toLocaleString('en-GB', {
      month: 'short'
    });
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };
  const getHoursRemaining = (dueDate: string) => {
    const now = new Date();
    const due = new Date(dueDate);
    const diffMs = due.getTime() - now.getTime();
    const diffHours = Math.ceil(diffMs / (1000 * 60 * 60));
    return diffHours;
  };
  const getDaysRemaining = (dueDate: string) => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const due = new Date(dueDate);
    due.setHours(0, 0, 0, 0);
    const diff = Math.ceil((due.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    return diff;
  };
  const getDaysLeftBadge = (dueDate: string) => {
    const hours = getHoursRemaining(dueDate);
    const days = getDaysRemaining(dueDate);
    // If overdue
    if (hours < 0) {
      const overdueHours = Math.abs(hours);
      if (overdueHours < 24) {
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800" data-id="element-6023">
            {t('pages.getDaysLeftBadge.overdue_by')} {overdueHours}{' '}
            {overdueHours === 1 ? 'hour' : 'hours'}
          </span>;
      } else {
        const overdueDays = Math.abs(days);
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800" data-id="element-6024">
            {t('pages.getDaysLeftBadge.overdue_by')} {overdueDays}{' '}
            {overdueDays === 1 ? 'day' : 'days'}
          </span>;
      }
    }
    // If due within 24 hours, show hours
    if (hours <= 24) {
      if (hours === 0) {
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800" data-id="element-6025">
            {t('pages.getDaysLeftBadge.due_in_less_than_1_hour')}
          </span>;
      } else if (hours === 1) {
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800" data-id="element-6026">
            {t('pages.ChallengeDispute.due_in_1_hour')}
          </span>;
      } else {
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800" data-id="element-6027">
            {t('pages.DisputeDetail.due_in')} {hours}{' '}
            {t('pages.getDaysLeftBadge.hours')}
          </span>;
      }
    }
    // Otherwise show days
    if (days <= 2) {
      return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800" data-id="element-6028">
          {days} {days === 1 ? 'day' : 'days'}{' '}
          {t('pages.getDaysLeftBadge.left')}
        </span>;
    } else if (days <= 7) {
      return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800" data-id="element-6029">
          {days} {t('pages.getDaysLeftBadge.days_left')}
        </span>;
    } else {
      return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-200 text-[#8C6C00]" data-id="element-6030">
          {days} {t('pages.getDaysLeftBadge.days_left')}
        </span>;
    }
  };
  const handleAcceptDispute = (disputeId: string) => {
    setOpenDropdownId(null);
    console.log('Accepting dispute:', disputeId);
    // In a real app, this would call an API
  };
  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ArrowUpDownIcon size={14} className="ml-1 text-gray-400" data-id="element-6031" />;
    }
    if (sortDirection === 'asc') {
      return <ArrowUpIcon size={14} className="ml-1 text-blue-600" data-id="element-6032" />;
    }
    return <ArrowDownIcon size={14} className="ml-1 text-blue-600" data-id="element-6033" />;
  };
  const handleRowClick = (disputeId: string, e: React.MouseEvent) => {
    // Don't navigate if clicking on the actions dropdown
    const target = e.target as HTMLElement;
    if (target.closest('.actions-dropdown')) {
      return;
    }
    navigate(`/dispute-detail/${disputeId}`);
  };
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const selectableDisputes = filteredDisputes.filter(d => d.status === 'requires_response').map(d => d.id);
      setSelectedDisputes(new Set(selectableDisputes));
    } else {
      setSelectedDisputes(new Set());
    }
  };
  const handleSelectDispute = (disputeId: string, checked: boolean) => {
    const newSelected = new Set(selectedDisputes);
    if (checked) {
      newSelected.add(disputeId);
    } else {
      newSelected.delete(disputeId);
    }
    setSelectedDisputes(newSelected);
  };
  const handleBulkAccept = () => {
    setShowBulkAcceptModal(true);
  };
  const confirmBulkAccept = () => {
    console.log('Accepting disputes:', Array.from(selectedDisputes));
    // In a real app, this would call an API
    setSelectedDisputes(new Set());
    setShowBulkAcceptModal(false);
  };
  const getReasonTooltip = (reason: DisputeReason): string => {
    const tooltips: Record<DisputeReason, string> = {
      credit_not_processed: 'The customer says they were owed a refund or credit, but it never showed up on their card statement.',
      duplicate: 'The customer believes they were charged more than once, or also paid another way, for the same purchase.',
      fraudulent: "The customer claims they didn't authorize or recognize this transaction on their card.",
      general: 'The bank believes the transaction was processed incorrectly (for example, wrong type or wrong amount).',
      product_not_received: 'The customer says they never received the goods or services by the expected date.',
      product_unacceptable: 'The customer says the goods or services were defective, damaged, or not as described.',
      subscription_canceled: 'The customer says they were charged again even though they believe the subscription was already canceled.',
      unrecognized: "The customer doesn't recognize this transaction.",
      incorrect_account_details: 'Incorrect account details were used for the transaction.',
      insufficient_funds: 'The account had insufficient funds for the transaction.',
      bank_cannot_process: 'The bank cannot process this transaction.',
      debit_not_authorized: 'The debit was not authorized by the customer.',
      customer_initiated: 'The dispute was initiated by the customer.',
      check_returned: 'The check was returned.',
      noncompliant: 'The transaction was noncompliant with regulations.'
    };
    return tooltips[reason] || '';
  };
  const selectableDisputesCount = filteredDisputes.filter(d => d.status === 'requires_response').length;
  const allSelectableSelected = selectableDisputesCount > 0 && selectedDisputes.size === selectableDisputesCount;
  const someSelected = selectedDisputes.size > 0 && !allSelectableSelected;
  return <div className="space-y-6" data-id="element-6034">
      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4" data-id="element-6035">
        <button onClick={() => handleQuickFilter('requires_response')} className={`bg-white p-4 rounded-lg border-2 shadow-sm text-left transition-all ${activeQuickFilter === 'requires_response' ? 'border-[#FFC70A] ring-2 ring-[#FFEC8A]' : 'border-gray-200 hover:border-[#FFD42E]'}`} data-id="element-6036">
          <div className="flex items-center justify-between" data-id="element-6037">
            <div data-id="element-6038">
              <div className="text-sm font-medium text-gray-500" data-id="element-6039">
                {t('pages.Disputes.requires_response')}
              </div>
              <div className="text-2xl font-semibold text-gray-900 mt-1" data-id="element-6040">
                {requiresResponseCount}
              </div>
            </div>
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${activeQuickFilter === 'requires_response' ? 'bg-[#FFE05C]' : 'bg-[#FFF4B8]'}`} data-id="element-6041">
              <AlertCircleIcon size={20} className="text-[#B38A00]" aria-hidden="true" data-id="element-6042" />
            </div>
          </div>
        </button>

        <button onClick={() => handleQuickFilter('challenged')} className={`bg-white p-4 rounded-lg border-2 shadow-sm text-left transition-all ${activeQuickFilter === 'challenged' ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-200 hover:border-blue-300'}`} data-id="element-6043">
          <div className="flex items-center justify-between" data-id="element-6044">
            <div data-id="element-6045">
              <div className="text-sm font-medium text-gray-500" data-id="element-6046">
                {t('pages.Disputes.challenged')}
              </div>
              <div className="text-2xl font-semibold text-gray-900 mt-1" data-id="element-6047">
                {challengedCount}
              </div>
            </div>
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${activeQuickFilter === 'challenged' ? 'bg-blue-200' : 'bg-blue-100'}`} data-id="element-6048">
              <ArrowUpRightIcon size={20} className="text-blue-600" data-id="element-6049" />
            </div>
          </div>
        </button>

        <button onClick={() => handleQuickFilter('all')} className={`bg-white p-4 rounded-lg border-2 shadow-sm text-left transition-all ${activeQuickFilter === 'all' ? 'border-gray-500 ring-2 ring-gray-200' : 'border-gray-200 hover:border-gray-400'}`} data-id="element-6050">
          <div className="flex items-center justify-between" data-id="element-6051">
            <div data-id="element-6052">
              <div className="text-sm font-medium text-gray-500" data-id="element-6053">
                {t('pages.Disputes.all_disputes')}
              </div>
              <div className="text-2xl font-semibold text-gray-900 mt-1" data-id="element-6054">
                {totalDisputes}
              </div>
            </div>
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${activeQuickFilter === 'all' ? 'bg-gray-200' : 'bg-gray-100'}`} data-id="element-6055">
              <AlertTriangleIcon size={20} className="text-gray-600" data-id="element-6056" />
            </div>
          </div>
        </button>

        <button onClick={() => handleQuickFilter('won_reversed')} className={`bg-white p-4 rounded-lg border-2 shadow-sm text-left transition-all ${activeQuickFilter === 'won_reversed' ? 'border-green-500 ring-2 ring-green-200' : 'border-gray-200 hover:border-green-300'}`} data-id="element-6057">
          <div className="flex items-center justify-between" data-id="element-6058">
            <div data-id="element-6059">
              <div className="text-sm font-medium text-gray-500" data-id="element-6060">
                {t('pages.Disputes.won_or_reversed')}
              </div>
              <div className="text-2xl font-semibold text-gray-900 mt-1" data-id="element-6061">
                {wonOrReversedCount}
              </div>
            </div>
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${activeQuickFilter === 'won_reversed' ? 'bg-green-200' : 'bg-green-100'}`} data-id="element-6062">
              <TrophyIcon size={20} className="text-green-600" data-id="element-6063" />
            </div>
          </div>
        </button>

        <button onClick={() => handleQuickFilter('accepted_lost')} className={`bg-white p-4 rounded-lg border-2 shadow-sm text-left transition-all ${activeQuickFilter === 'accepted_lost' ? 'border-red-500 ring-2 ring-red-200' : 'border-gray-200 hover:border-red-300'}`} data-id="element-6064">
          <div className="flex items-center justify-between" data-id="element-6065">
            <div data-id="element-6066">
              <div className="text-sm font-medium text-gray-500" data-id="element-6067">
                {t('pages.Disputes.accepted_or_lost')}
              </div>
              <div className="text-2xl font-semibold text-gray-900 mt-1" data-id="element-6068">
                {acceptedOrLostCount}
              </div>
            </div>
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${activeQuickFilter === 'accepted_lost' ? 'bg-red-200' : 'bg-red-100'}`} data-id="element-6069">
              <XCircleIcon size={20} className="text-red-600" data-id="element-6070" />
            </div>
          </div>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white shadow-sm rounded-lg border border-gray-200" data-id="element-6071">
        <div className="p-4 border-b border-gray-200 bg-gray-50" data-id="element-6072">
          <div className="flex flex-col gap-4" data-id="element-6073">
            {/* Search bar */}
            <div className="relative flex-grow" data-id="element-6074">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-6075">
                <SearchIcon size={16} className="text-gray-400" data-id="element-6076" />
              </div>
              <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder={t('pages.Disputes.attrs.search_by_dispute_id_transaction_id')} data-id="element-6077" />
            </div>

            {/* Dropdown filters */}
            <div className="flex gap-2 flex-wrap" data-id="element-6078">
              <select value={stageFilter} onChange={e => setStageFilter(e.target.value as DisputeStage | 'all')} className="block pl-3 pr-8 py-2 text-base border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 rounded-md" data-id="element-6079">
                <option value="all" data-id="element-6080">{t('pages.Disputes.all_stages')}</option>
                <option value="rfi" data-id="element-6081">{t('pages.Disputes.rfi')}</option>
                <option value="pre_chargeback" data-id="element-6082">
                  {t('pages.Disputes.prechargeback')}
                </option>
                <option value="chargeback" data-id="element-6083">
                  {t('pages.Disputes.chargeback')}
                </option>
                <option value="pre_arbitration" data-id="element-6084">
                  {t('pages.Disputes.prearbitration')}
                </option>
                <option value="arbitration" data-id="element-6085">
                  {t('pages.Disputes.arbitration')}
                </option>
              </select>

              <select value={statusFilter} onChange={e => setStatusFilter(e.target.value as DisputeStatus | 'all')} className="block pl-3 pr-8 py-2 text-base border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 rounded-md" data-id="element-6086">
                <option value="all" data-id="element-6087">{t('pages.Disputes.all_statuses')}</option>
                <option value="requires_response" data-id="element-6088">
                  {t('pages.Disputes.requires_response')}
                </option>
                <option value="expired" data-id="element-6089">{t('pages.Disputes.expired')}</option>
                <option value="challenged" data-id="element-6090">
                  {t('pages.Disputes.challenged')}
                </option>
                <option value="accepted" data-id="element-6091">{t('pages.Disputes.accepted')}</option>
                <option value="reversed" data-id="element-6092">{t('pages.Disputes.reversed')}</option>
                <option value="pending_closure" data-id="element-6093">
                  {t('pages.Disputes.pending_closure')}
                </option>
                <option value="pending_decision" data-id="element-6094">
                  {t('pages.Disputes.pending_decision')}
                </option>
                <option value="won" data-id="element-6095">{t('pages.Disputes.won')}</option>
                <option value="lost" data-id="element-6096">{t('pages.Disputes.lost')}</option>
              </select>

              <select value={reasonFilter} onChange={e => setReasonFilter(e.target.value as DisputeReason | 'all')} className="block pl-3 pr-8 py-2 text-base border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 rounded-md" data-id="element-6097">
                <option value="all" data-id="element-6098">{t('pages.Disputes.all_reasons')}</option>
                <option value="duplicate" data-id="element-6099">
                  {t('pages.Disputes.duplicate')}
                </option>
                <option value="fraudulent" data-id="element-6100">
                  {t('pages.Disputes.fraudulent')}
                </option>
                <option value="subscription_canceled" data-id="element-6101">
                  {t('pages.Disputes.subscription_canceled')}
                </option>
                <option value="product_unacceptable" data-id="element-6102">
                  {t('pages.Disputes.product_unacceptable')}
                </option>
                <option value="product_not_received" data-id="element-6103">
                  {t('pages.Disputes.product_not_received')}
                </option>
                <option value="unrecognized" data-id="element-6104">
                  {t('pages.Disputes.unrecognized')}
                </option>
                <option value="credit_not_processed" data-id="element-6105">
                  {t('pages.Disputes.credit_not_processed')}
                </option>
                <option value="incorrect_account_details" data-id="element-6106">
                  {t('pages.Disputes.incorrect_account_details')}
                </option>
                <option value="insufficient_funds" data-id="element-6107">
                  {t('pages.Disputes.insufficient_funds')}
                </option>
                <option value="bank_cannot_process" data-id="element-6108">
                  {t('pages.Disputes.bank_cannot_process')}
                </option>
                <option value="debit_not_authorized" data-id="element-6109">
                  {t('pages.Disputes.debit_not_authorized')}
                </option>
                <option value="general" data-id="element-6110">{t('pages.Disputes.general')}</option>
                <option value="customer_initiated" data-id="element-6111">
                  {t('pages.Disputes.customer_initiated')}
                </option>
                <option value="check_returned" data-id="element-6112">
                  {t('pages.Disputes.check_returned')}
                </option>
                <option value="noncompliant" data-id="element-6113">
                  {t('pages.Disputes.noncompliant')}
                </option>
              </select>

              <select value={paymentMethodFilter} onChange={e => setPaymentMethodFilter(e.target.value)} className="block pl-3 pr-8 py-2 text-base border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 rounded-md" data-id="element-6114">
                <option value="all" data-id="element-6115">
                  {t('pages.Disputes.all_payment_methods')}
                </option>
                <option value="card" data-id="element-6116">{t('pages.Disputes.card')}</option>
                <option value="bank_transfer" data-id="element-6117">
                  {t('pages.Disputes.bank_transfer')}
                </option>
                <option value="wallet" data-id="element-6118">{t('pages.Disputes.wallet')}</option>
              </select>
            </div>

            {/* Date filters */}
            <div className="flex gap-2 flex-wrap items-end" data-id="element-6119">
              <div className="flex-shrink-0" data-id="element-6120">
                <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-6121">
                  {t('pages.Disputes.filter_by')}
                </label>
                <select value={dateFilterType} onChange={e => setDateFilterType(e.target.value as DateFilterType)} className="block pl-3 pr-8 py-2 text-base border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 rounded-md" data-id="element-6122">
                  <option value="response_due" data-id="element-6123">
                    {t('pages.ChallengeDispute.response_due')}
                  </option>
                  <option value="disputed_time" data-id="element-6124">
                    {t('pages.Disputes.disputed_time')}
                  </option>
                  <option value="last_updated" data-id="element-6125">
                    {t('pages.Disputes.last_updated')}
                  </option>
                </select>
              </div>

              <div className="flex-shrink-0" data-id="element-6126">
                <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-6127">
                  {t('pages.Disputes.from')}
                </label>
                <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="block px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" data-id="element-6128" />
              </div>

              <div className="flex-shrink-0" data-id="element-6129">
                <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-6130">
                  {t('pages.Disputes.to')}
                </label>
                <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="block px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" data-id="element-6131" />
              </div>

              {(dateFrom || dateTo) && <button onClick={() => {
              setDateFrom('');
              setDateTo('');
            }} className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-6132">
                  {t('pages.Disputes.clear_dates')}
                </button>}
            </div>
          </div>
        </div>

        {/* Disputes List */}
        <div className="overflow-x-auto" data-id="element-6133">
          {selectedDisputes.size > 0 && <div className="px-6 py-3 bg-blue-50 border-b border-blue-200 flex items-center justify-between" data-id="element-6134">
              <div className="flex items-center gap-3" data-id="element-6135">
                <span className="text-sm font-medium text-blue-900" data-id="element-6136">
                  {selectedDisputes.size} {t('pages.Disputes.dispute')}
                  {selectedDisputes.size !== 1 ? 's' : ''}{' '}
                  {t('pages.Disputes.selected')}
                </span>
                <button onClick={() => setSelectedDisputes(new Set())} className="text-sm text-blue-600 hover:text-blue-800" data-id="element-6137">
                  {t('pages.Disputes.clear_selection')}
                </button>
              </div>
              <button onClick={handleBulkAccept} className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700" data-id="element-6138">
                <CheckIcon size={16} className="mr-2" data-id="element-6139" />
                {t('pages.Disputes.accept_selected')}
              </button>
            </div>}
          <table className="min-w-full divide-y divide-gray-200" data-id="element-6140">
            <thead className="bg-gray-50" data-id="element-6141">
              <tr data-id="element-6142">
                <th className="px-6 py-3 text-left" data-id="element-6143">
                  <input type="checkbox" checked={allSelectableSelected} ref={el => {
                  if (el) {
                    el.indeterminate = someSelected;
                  }
                }} onChange={e => handleSelectAll(e.target.checked)} disabled={selectableDisputesCount === 0} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-6144" />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('dueDate')} data-id="element-6145">
                  <div className="flex items-center" data-id="element-6146">
                    {t('pages.Disputes.response_due')}
                    {getSortIcon('dueDate')}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('amount')} data-id="element-6147">
                  <div className="flex items-center" data-id="element-6148">
                    {t('pages.Disputes.disputed_amount')}
                    {getSortIcon('amount')}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('reason')} data-id="element-6149">
                  <div className="flex items-center" data-id="element-6150">
                    {t('pages.DisputeDetail.reason')}
                    {getSortIcon('reason')}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('stage')} data-id="element-6151">
                  <div className="flex items-center" data-id="element-6152">
                    {t('pages.Disputes.stage')}
                    {getSortIcon('stage')}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('status')} data-id="element-6153">
                  <div className="flex items-center" data-id="element-6154">
                    {t('pages.DisputeDetail.status')}
                    {getSortIcon('status')}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('paymentMethod')} data-id="element-6155">
                  <div className="flex items-center" data-id="element-6156">
                    {t('pages.Disputes.payment_method')}
                    {getSortIcon('paymentMethod')}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('customerName')} data-id="element-6157">
                  <div className="flex items-center" data-id="element-6158">
                    {t('pages.Disputes.customer')}
                    {getSortIcon('customerName')}
                  </div>
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6159">
                  {t('pages.Disputes.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-6160">
              {filteredDisputes.map(dispute => {
              const isSelectable = dispute.status === 'requires_response';
              const isSelected = selectedDisputes.has(dispute.id);
              return <tr key={dispute.id} className="hover:bg-gray-50 cursor-pointer" onClick={e => handleRowClick(dispute.id, e)} data-id="element-6161">
                    <td className="px-6 py-4" onClick={e => e.stopPropagation()} data-id="element-6162">
                      <input type="checkbox" checked={isSelected} onChange={e => handleSelectDispute(dispute.id, e.target.checked)} disabled={!isSelectable} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-30" data-id="element-6163" />
                    </td>
                    <td className="px-6 py-4" data-id="element-6164">
                      <div className="text-sm text-gray-900" data-id="element-6165">
                        {formatDate(dispute.dueDate)}
                      </div>
                      <div className="mt-1" data-id="element-6166">
                        {getDaysLeftBadge(dispute.dueDate)}
                      </div>
                    </td>
                    <td className="px-6 py-4" data-id="element-6167">
                      <div className="text-sm font-medium text-gray-900" data-id="element-6168">
                        {dispute.currency}{' '}
                        {(dispute.amount / 100).toLocaleString('en-US', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-6169">
                        {dispute.disputeId}
                      </div>
                    </td>
                    <td className="px-6 py-4" data-id="element-6170">
                      <div className="flex items-center gap-1" data-id="element-6171">
                        <span className="text-sm text-gray-900" data-id="element-6172">
                          {getReasonLabel(dispute.reason)}
                        </span>
                        <div className="relative inline-block" data-id="element-6173">
                          <div onMouseEnter={() => setHoveredReason(dispute.id)} onMouseLeave={() => setHoveredReason(null)} className="cursor-help" data-id="element-6174">
                            <InfoIcon size={14} className="text-gray-400" data-id="element-6175" />
                          </div>
                          {hoveredReason === dispute.id && <div className="absolute z-50 w-72 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-lg bottom-full left-1/2 transform -translate-x-1/2 mb-2" data-id="element-6176">
                              {getReasonTooltip(dispute.reason)}
                              <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1" data-id="element-6177">
                                <div className="border-4 border-transparent border-t-gray-900" data-id="element-6178"></div>
                              </div>
                            </div>}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4" data-id="element-6179">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-6180">
                        {getStageLabel(dispute.stage)}
                      </span>
                    </td>
                    <td className="px-6 py-4" data-id="element-6181">
                      {getStatusBadge(dispute.status)}
                    </td>
                    <td className="px-6 py-4" data-id="element-6182">
                      <div className="text-sm text-gray-900" data-id="element-6183">
                        {getPaymentMethodLabel(dispute.paymentMethod)}
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-6184">
                        {dispute.cardBrand} •••• {dispute.cardLast4}
                      </div>
                    </td>
                    <td className="px-6 py-4" data-id="element-6185">
                      <div data-id="element-6186">
                        <div className="text-sm font-medium text-gray-900" data-id="element-6187">
                          {dispute.customerName}
                        </div>
                        <div className="text-sm text-gray-500" data-id="element-6188">
                          {dispute.customerEmail}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right" onClick={e => e.stopPropagation()} data-id="element-6189">
                      <div className="relative inline-block actions-dropdown" data-id="element-6190">
                        <button onClick={() => setOpenDropdownId(openDropdownId === dispute.id ? null : dispute.id)} aria-label="Dispute actions" aria-expanded={openDropdownId === dispute.id} className="p-2 hover:bg-gray-100 rounded-md transition-colors" data-id="element-6191">
                          <MoreVerticalIcon size={18} className="text-gray-600" aria-hidden="true" data-id="element-6192" />
                        </button>

                        {openDropdownId === dispute.id && <>
                            <div className="fixed inset-0 z-10" onClick={() => setOpenDropdownId(null)} data-id="element-6193" />

                            <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20" data-id="element-6194">
                              {dispute.status === 'challenged' && <button onClick={() => {
                          setOpenDropdownId(null);
                          navigate(`/dispute-detail/${dispute.id}`);
                        }} className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" data-id="element-6195">
                                  <FileTextIcon size={16} className="mr-3 text-gray-500" data-id="element-6196" />

                                  {t('pages.DisputeDetail.view_challenge_details')}
                                </button>}
                              {dispute.status === 'requires_response' && <button onClick={() => handleAcceptDispute(dispute.id)} className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" data-id="element-6197">
                                  <CheckIcon size={16} className="mr-3 text-gray-500" data-id="element-6198" />

                                  {t('pages.DisputeDetail.accept')}
                                </button>}
                              <button onClick={() => {
                          setOpenDropdownId(null);
                          navigate(`/dispute-detail/${dispute.id}`);
                        }} className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" data-id="element-6199">
                                <EyeIcon size={16} className="mr-3 text-gray-500" data-id="element-6200" />

                                {t('pages.ChallengeDispute.view_details')}
                              </button>
                            </div>
                          </>}
                      </div>
                    </td>
                  </tr>;
            })}
            </tbody>
          </table>
        </div>

        {filteredDisputes.length === 0 && <div className="py-12 flex flex-col items-center justify-center text-center" data-id="element-6201">
            <AlertTriangleIcon size={48} className="text-gray-400 mb-4" data-id="element-6202" />
            <h3 className="text-lg font-medium text-gray-900 mb-1" data-id="element-6203">
              {t('pages.Disputes.no_disputes_found')}
            </h3>
            <p className="text-gray-500 max-w-md" data-id="element-6204">
              {t('pages.Disputes.no_disputes_match_your_current_search')}
            </p>
          </div>}
      </div>

      {/* Bulk Accept Confirmation Modal */}
      {showBulkAcceptModal && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-6205">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-6206">
            <div className="fixed inset-0 transition-opacity" onClick={() => setShowBulkAcceptModal(false)} data-id="element-6207">
              <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-6208"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-6209">
              &#8203;
            </span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full" data-id="element-6210">
              <div className="bg-white px-6 pt-6 pb-4" data-id="element-6211">
                <div className="flex items-start gap-4" data-id="element-6212">
                  <div className="flex-shrink-0" data-id="element-6213">
                    <div className="w-12 h-12 rounded-full bg-[#FFE05C] flex items-center justify-center" data-id="element-6214">
                      <AlertTriangleIcon size={24} className="text-[#B38A00]" aria-hidden="true" data-id="element-6215" />
                    </div>
                  </div>
                  <div className="flex-1" data-id="element-6216">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-6217">
                      {t('pages.DisputeDetail.accept')} {selectedDisputes.size}{' '}
                      {t('pages.Disputes.dispute')}
                      {selectedDisputes.size !== 1 ? 's' : ''}?
                    </h3>
                    <p className="text-sm text-gray-600" data-id="element-6218">
                      {t('pages.Disputes.accepting')}{' '}
                      {selectedDisputes.size === 1 ? 'this dispute' : 'these disputes'}{' '}
                      {t('pages.Disputes.is_final_once_you_accept_the')}
                      {selectedDisputes.size !== 1 ? 's' : ''}{' '}
                      {t('pages.Disputes.will_receive_a_refund_the_funding')}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 px-6 py-4 flex items-center justify-end gap-3" data-id="element-6219">
                <button onClick={() => setShowBulkAcceptModal(false)} className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900" data-id="element-6220">
                  {t('pages.ChallengeDispute.cancel')}
                </button>
                <button onClick={confirmBulkAccept} className="px-6 py-2 text-sm font-medium text-white bg-orange-600 rounded-md hover:bg-orange-700" data-id="element-6221">
                  {t('pages.DisputeDetail.accept_dispute')}
                  {selectedDisputes.size !== 1 ? 's' : ''}
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}